import Table from '@shared/components/Table';
import TemplateDocumentsForm from './TemplateDocumentsForm';
import DeleteModal from '@shared/components/DeleteModal';
import ActionButtons from '@shared/components/ActionButtons';

import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import useModal from '@shared/hooks/useModal';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import { useState, useEffect, useCallback } from 'react';
import { templateDocumentsService } from '../services/templateDocuments';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useTranslation } from 'react-i18next';

export default function TemplateDocuments() {
    const { t } = useTranslation();
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const columns = [
        {
            label: t('nav.administrator.templateDocuments.name'),
            key: 'name',
        },
        {
            label: t('nav.administrator.templateDocuments.description'),
            key: 'description',
        },
    ];

    const fetchTemplateDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const response = await templateDocumentsService.getTemplateDocumentsList();
            setData(response);
        } catch (error) {   
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplateDocuments();
    }, []);

    const handleConfirmDelete = useDeleteHandler({
        service: templateDocumentsService,
        entityName: 'TemplateDocument',
        selectedData: selectedRecord,
        fetchData: fetchTemplateDocuments,
        setSelectedData: setSelectedRecord,
        handleCloseModal,
    });

    const actionButtons = (
        <ActionButtons
            buttons={[
                { ...COMMON_BUTTONS.ADD, onClick: () => handleOpenModal('add') },
                { ...COMMON_BUTTONS.EDIT, onClick: () => handleOpenModal('edit') },
                { ...COMMON_BUTTONS.DELETE, onClick: () => handleOpenModal('delete') },
            ]}
            selectedRecord={selectedRecord}
        />
    );

    return (
        <>
            <Table
                title={t('nav.administrator.templateDocuments.title')}
                columns={columns}
                data={data}
                loading={loading}
                selectedData={selectedRecord?.id}
                actionButtons={actionButtons}
                showPagination={false}
                showPageSizeSelector={false}
                showCustomSearch={true}
                showActiveSelect={false}
                showCompanySelect={false}
                showLevelSelect={false}
                showPreviousPage={true}
                showCountriesSelect={false}
                showNomenclaturesSelect={false}
                handleRowClick={(index) => {
                    const current = data || [];
                    if (index >= 0 && index < current.length) {
                        setSelectedRecord(current[index]);
                    }
                }}
            />
            {modalState.visible && modalState.type !== 'delete' && (
                <TemplateDocumentsForm
                    onClose={handleCloseModal}
                    modalType={modalState.type}
                    selectedRecord={selectedRecord}
                    onConfirm={fetchTemplateDocuments}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.name || selectedRecord?.description || ''}
                    entityType="record"
                />
            )}
        </>
    );
}